/**
 * @file: The sentences that the panel shows about the screen.
 *
 * The panel tells the operator what a save puts on screen. The role note names
 * the intervals that this one takes the screen from, or loses it to. The
 * screen notes say how much of the run reaches the screen, and which intervals
 * win the rest. Each sentence is a function of the standing and the panel
 * fields that it reads, so a spec can test it without a panel.
 */

import { SCREEN_SHARE } from '../calendar-intervals'
import { namesInProse, verbFor } from '../names-in-prose'
import { coveredByOwnPlaylist, ownPlaylistCoverSentences } from '../own-playlist-cover'

/** The operator selects one of these roles. The play button writes the third role. */
const SELECTABLE_ROLES = Object.freeze(['basis', 'takeover'])

/**
 * Write one sentence on what the selected role changes at these times.
 *
 * A role decides only the order against the intervals that overlap this
 * one. An interval alone on the grid shows the same with either role. A
 * band peer is in the shared-stretch section, so this sentence leaves it
 * out. The server raises a new interval above its peers. The standing of a
 * new interval therefore gives those peers as under it.
 *
 * @param {string|null} role - The role in the panel
 * @param {Object} standing - The standing of the panel values
 * @returns {string} The sentence, or an empty string
 */
function roleNote (role, standing) {
  if (!SELECTABLE_ROLES.includes(role)) return ''

  // Without a standing the overlaps are unknown, and not absent.
  if (!standing.known) return ''

  const { peers, under, over } = standing

  if (peers.length + under.length + over.length === 0) {
    return 'Nothing else is scheduled at this time. The role changes nothing now.'
  }

  const below = under.map(item => item.title)
  const above = over.map(item => item.title)

  return [
    below.length ? `Takes the screen from ${namesInProse(below)} where they overlap.` : '',
    above.length ? `${namesInProse(above)} ${verbFor(above, 'takes', 'take')} the screen from this interval where they overlap.` : ''
  ].filter(Boolean).join(' ')
}

/**
 * The names of the band peers that share the stretch, in prose.
 *
 * @param {Array<{title: string}>} peers - The peers of the standing
 * @returns {string}
 */
function peerNames (peers) {
  return namesInProse(peers.map(peer => peer.title))
}

/**
 * The sentence for an interval that is on screen for part of its run. It
 * names the intervals that win the rest, so the operator knows who has the
 * screen then. A rest without a winner names nobody, and the strip shows it.
 *
 * @param {Object|null} screen - The screen answer of the standing
 * @returns {string} The sentence, or an empty string for another share
 */
function partOnScreenNote (screen) {
  if (!screen || screen.share !== SCREEN_SHARE.PART) return ''

  const coveredBy = screen.coveredBy

  if (coveredBy.length === 0) return 'On screen for part of this interval. The strip below shows when.'

  return `On screen for part of this interval. ${namesInProse(coveredBy)} ${verbFor(coveredBy, 'is', 'are')} on screen for the rest.`
}

/**
 * The sentence for an interval that never reaches the screen. It names the
 * intervals that win the run, so the operator knows which bar to change.
 *
 * A cover by the own playlist gets the words of the Preflight card. All the
 * covering intervals then have one name.
 *
 * @param {Object|null} screen - The screen answer of the standing
 * @param {number|string|null} sceneCollectionId - The playlist in the panel
 * @returns {string} The sentence, or an empty string for another share
 */
function neverOnScreenNote (screen, sceneCollectionId) {
  if (!screen || screen.share !== SCREEN_SHARE.NEVER) return ''

  const coveredBy = screen.coveredBy

  if (coveredBy.length === 0) return 'Never on screen.'

  if (coveredByOwnPlaylist(screen.coveredByPlaylistIds, sceneCollectionId)) {
    const { cause, remedy } = ownPlaylistCoverSentences(coveredBy[0])

    return `Never on screen. ${cause} ${remedy}`
  }

  return `Never on screen. ${namesInProse(coveredBy)} ${verbFor(coveredBy, 'covers', 'cover')} the whole interval.`
}

/**
 * The note box for each SCREEN_SHARE value. Each entry has a tone, an icon and
 * a function that gives the text from the screen answer and the playlist. The
 * part note and the never note name the intervals that win the rest of the run.
 *
 * The entries are constants, so an Angular watch on one sees no change between
 * two digests.
 */
const SCREEN_SHARE_NOTES = Object.freeze({
  [SCREEN_SHARE.WHOLE]: Object.freeze({
    tone: 'is-ok',
    icon: 'fa-check',
    text: () => 'On screen for the whole interval.'
  }),
  [SCREEN_SHARE.PART]: Object.freeze({
    tone: 'is-ok',
    icon: 'fa-circle-half-stroke',
    text: partOnScreenNote
  }),
  [SCREEN_SHARE.NEVER]: Object.freeze({
    tone: 'is-warn',
    icon: 'fa-triangle-exclamation',
    text: neverOnScreenNote
  })
})

export {
  SCREEN_SHARE_NOTES,
  roleNote,
  peerNames,
  partOnScreenNote,
  neverOnScreenNote
}
